import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@territory/client/AuthContext';
import { bootstrapWedgeAuth, setWedgeTokens } from '@territory/client/api';
import { clearWedgeLocalState } from './clearWedgeState';
import { Splash } from './Splash';
import { initTrackingTags, trackSignupLead } from './wedgeTrackingTags';

/**
 * Landing route after the backend OAuth callback redirects to
 * https://wedge.repvantage.io/auth/callback. AuthProvider has already fired
 * its session check on mount (httpOnly cookie is set on this host by now);
 * once it resolves we route into the wedge, or bounce back to sign-in if the
 * session did not stick (e.g. a non-@repvantage.io account was rejected).
 */
export function AuthCallbackWedge() {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      /* 2026-05-29 · brand-new signup (backend appends ?fresh=1) gets a clean
         device slate so a prior account's cached onboarding-complete flag on
         this browser can't skip the strategically critical onboarding. Clears
         only the rv_wedge_* namespace (keeps theme + device tag); the session
         is in httpOnly cookies so this never signs them out. Returning users
         (no ?fresh) keep their cached state. */
      try {
        if (new URLSearchParams(window.location.search).get('fresh') === '1') {
          /* 2026-08-26 · Bug 9B. On the native iOS shell the session lives ONLY
             in the rv_wedge_at/rt localStorage bridge (capacitor://localhost
             carries no auth cookie). clearWedgeLocalState() wipes the whole
             rv_wedge_* namespace, including the tokens minted milliseconds ago
             for THIS new account, and with no cookie to fall back on, the fresh
             signup got bounced to /login right after finishing onboarding.
             Preserve the just-minted session across the onboarding-cache wipe.
             Both reads are null on web, so web behavior is byte-identical. */
          const _at = localStorage.getItem('rv_wedge_at');
          const _rt = localStorage.getItem('rv_wedge_rt');
          clearWedgeLocalState();
          if (_at || _rt) setWedgeTokens(_at || undefined, _rt || undefined);
          // Ad conversion tracking - fire the Lead event exactly once, at the
          // true signup moment. This is a full-page OAuth redirect (never
          // routed through WedgeAuthGate), so the base tags haven't
          // necessarily loaded on this page yet - init them here first.
          initTrackingTags();
          trackSignupLead();
        }
      } catch {
        /* storage blocked. Server prefs still gate onboarding */
      }
      // BOOTSTRAP the wedge token bridge via the SHARED single-flight refresh
      // (api.ts bootstrapWedgeAuth → sharedRefresh). Critical: this must NOT
      // be a raw independent /auth/refresh — that raced the app's first
      // refresh on the same single-use cookie token, tripping backend
      // reuse-detection → family revoked at login → every
      // /accounts/research/stream 401'd → deep scrape never ran (the stub
      // bug). Coalesced through sharedRefresh, the cookie token is spent
      // exactly once; rotated body tokens get stored for the standalone
      // session. Resilient: never blocks sign-in.
      /* 2026-06-02 · round 19 · B1 · honor ?return= forwarded by the
         OAuth callback. Backend validated same-origin at mint AND
         consume (cache state JSON). One final defense-in-depth check
         here · anything that doesn't start with / falls through to the
         default root navigation, so a corrupted query string can never
         become an open-redirect attack. */
      let dest = '/';
      try {
        const ret = new URLSearchParams(window.location.search).get('return') || '';
        if (/^\/[^/]/.test(ret) || ret === '/') dest = ret;
      } catch { /* fall through to / */ }
      bootstrapWedgeAuth()
        .catch(() => { /* non-fatal · proceed into the app */ })
        .finally(() => navigate(dest, { replace: true }));
    } else {
      // Cookie did not stick (rejected account / race). Do NOT re-bounce to
      // Google — that risks an invisible loop. Land on the terminal screen.
      // 2026-08-10 (SIWA pending-account incident) · carry the specific cause
      // when the auth check saw "Account is not active", so the terminal
      // screen can say the truth instead of coaching an endless retry.
      let deniedReason = 'denied';
      try {
        if (sessionStorage.getItem('rv_auth_denied_reason') === 'pending') deniedReason = 'pending';
      } catch { /* storage blocked · generic reason */ }
      navigate(`/login?error=${deniedReason}`, { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  return <Splash label="Signing you in" />;
}
